// src/pages/UmatData.js
import React, { useEffect, useMemo, useState } from "react";
import ContentLayout from "../layouts/ContentLayout";
import { useHistory } from "react-router-dom";
import { usePinGuard } from '../utils/usePinGuard';
import PERMISSIONS, { apiFetch, hasUmatPermission, resolveUmatAvatarSync, getUmatDisplayLabel, getUmatDisplayLabelLine2, getUmatMetaLine } from '../utils/wkCommon';
import { getJwt, getUser } from '../utils/auth';
import CardUmat from "../components/CardUmat";
import DetailUmat from "../components/DetailUmat";
import { isZh } from "../utils/language";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// helper untuk membuat absolute url untuk media Strapi
function absoluteUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return API_URL + url;
}

// normalize item dari Strapi (handle {id, attributes} atau flattened)
function normalizeItem(item) {
  if (!item) return null;
  if (item.id && item.attributes) {
    return { id: item.id, ...item.attributes };
  }
  return { ...item };
}

// build URL untuk fetch dengan filter server-side (OR across fields)
function buildUmatsUrl({ keyword = "", page = 1, pageSize = 50 } = {}) {
  const base = `${API_URL}/api/umats`;
  const params = new URLSearchParams();
  //params.append("populate", "*");
  params.append("fields[0]", "umatID");
  params.append("fields[1]", "chineseName");
  params.append("fields[2]", "pinyinName");
  params.append("fields[3]", "fullName");
  params.append("fields[4]", "nickName");
  params.append("fields[5]", "gender");
  params.append("fields[6]", "birthdate");
  params.append("fields[7]", "address");
  params.append("fields[8]", "mobilePhone");
  params.append("fields[9]", "email");
  params.append("fields[10]", "LoginEmail");
  params.append("fields[11]", "fatherName");
  params.append("fields[12]", "fatherPhoneNo");
  params.append("fields[13]", "motherName");
  params.append("fields[14]", "motherPhoneNo");
  params.append("fields[15]", "tsfyUmatID");

  // butuh area:
  params.append("populate[0]", "area");
  params.append("populate[area][fields][0]", "name");
  params.append("populate[area][fields][1]", "code");
  params.append("populate[area][fields][2]", "shortname");

  // data orangtua
  params.append("populate[father_umat_item][fields][0]", "fullName");
  params.append("populate[father_umat_item][fields][1]", "nickName");
  params.append("populate[father_umat_item][fields][2]", "chineseName");
  params.append("populate[father_umat_item][fields][3]", "mobilePhone");

  params.append("populate[mother_umat_item][fields][0]", "fullName");
  params.append("populate[mother_umat_item][fields][1]", "nickName");
  params.append("populate[mother_umat_item][fields][2]", "chineseName");
  params.append("populate[mother_umat_item][fields][3]", "mobilePhone");

  // profilepic:
  params.append("populate[profilepic]", "true");

  params.append("pagination[page]", String(page));
  params.append("pagination[pageSize]", String(pageSize));
  params.append("filters[$and][0][$or][0][passedAway][$eq]", "false");
  params.append("filters[$and][0][$or][1][passedAway][$null]", "true");
  params.append("filters[$and][1][$or][0][dianchuanshi][$eq]", "false");
  params.append("filters[$and][1][$or][1][dianchuanshi][$null]", "true");

  const q = (keyword || "").trim();
  if (!q) {
    return `${base}?${params.toString()}`;
  }

  if(hasHan(q))
  {
    // fields to search (OR)
    const fields = ["chineseName"];
    fields.forEach((field, idx) => {
      // use case-insensitive contains
      const key = `filters[$or][${idx}][${field}][$containsi]`;
      params.append(key, q);
    });
  }
  else {
    // fields to search (OR)
    const fields = ["umatID", "pinyinName", "fullName", "nickName", "email"];
    fields.forEach((field, idx) => {
      // use case-insensitive contains
      const key = `filters[$or][${idx}][${field}][$containsi]`;
      params.append(key, q);
    });
  }

  return `${base}?${params.toString()}`;
}

// --- helper: apakah query memenuhi syarat pencarian ---
function hasHan(q)
{
  // check for at least one Han character (Chinese)
  const hasHan = /\p{Script=Han}/u.test(q);
  if (hasHan) return true;
  else return false;
}

// minimal 3 karakter latin (A-Z / a-z / 0-9 counted as latin) OR minimal 1 karakter Han (Mandarin)
function isSearchable(q) {
  if (!q) return false;
  const s = q.trim();
  if (hasHan(s)) return true;
  // count latin letters/digits (A-Z a-z 0-9). treat other latin-like as not counted.
  const latinMatches = s.match(/[A-Za-z0-9]/g) || [];
  return latinMatches.length >= 3;
}

export default function DataUmat() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null); // pagination meta
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50); // adjust as needed (max Strapi default often 100)
  const [error, setError] = useState(null);
  const history = useHistory();

  const [canViewUmatAge, setCanViewUmatAge] = useState(false);
  useEffect(() => {
    let mounted = true;

    async function loadPerms() {
      const ok = await hasUmatPermission(
        getUser()?.email,
        PERMISSIONS.UMATDATA_FULL
      );
      if (mounted) setCanViewUmatAge(!!ok);
    }

    loadPerms();
    return () => { mounted = false; };
  }, []);

  /* PIN Verification */
  /*
    import { usePinGuard } from '../utils/usePinGuard';
    import { PinModal } from '../components/PinModal';
  */
  const { needsPin, setNeedsPin } = usePinGuard(60); // 60 menit idle limit

  useEffect(() => {
    let mounted = true;
    async function load() {
      // only run search if searchValue fulfills isSearchable
      if (!isSearchable(searchValue)) {
        // reset state to "empty" when search is not active
        setRows([]);
        setMeta(null);
        setError(null);
        setLoading(false);
        setSelected(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const url = buildUmatsUrl({ keyword: searchValue, page, pageSize });

        const jwt = getJwt();
        const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};
        //const resp = await fetch(url, { headers }); // <-- NO credentials
        //if (!resp.ok) {
        //  const text = await resp.text();
        //  throw new Error(`HTTP ${resp.status}: ${text}`);
        //}
        //const json = await resp.json();
        const json = await apiFetch(url);
        const rawArr = Array.isArray(json) ? json : (json.data || []);
        const normalized = rawArr.map(normalizeItem).map(i => ({
          id: i.id || i.umatID || Math.random(),
          umatID: i.umatID || i.code || "",
          chineseName: i.chineseName || i.chinese || "",
          pinyinName: i.pinyinName || i.pinyin || "",
          fullName: i.fullName || i.displayName || i.name || "",
          nickName: i.nickName || i.nickname || "",
          district: i.district || i.fotangWilayah || i.fotang || "",
          gender: i.gender || "",
          birthdate: i.birthdate || i.tanggalLahir || "",
          address: i.address || "",
          mobilePhone: i.mobilePhone || i.mobile || i.phone || "",
          email: i.email || "",
          LoginEmail: i.LoginEmail || "",
          raw: i,
        }));
        if (!mounted) return;
        setRows(normalized);
        setMeta(json.meta || null);
      } catch (err) {
        console.error("Fetch error:", err);
        if (mounted) setError(String(err));
        setRows([]);
        setMeta(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [searchValue, page, pageSize]);

  // filtered is server-side; local memo for UI
  const listNode = useMemo(() => {
    // if search is not active yet, show helper text (instead of fetching everything)
    if (!isSearchable(searchValue)) {
      return (
        <div className="empty">
          {
            isZh() ?
            <>請輸入至少 <strong>3 個英數字</strong>或 <strong>1 個中文字</strong>符以開始搜尋。</> :
            <>Masukkan pencarian minimal <strong>3 karakter latin</strong> atau <strong>1 karakter Mandarin</strong> untuk mulai.</>
          }
        </div>
      );
    }

    if (loading) return <div className="empty">{isZh() ? "載入中…" : "Loading..."}</div>;
    if (error) return <div className="empty" style={{ color: "crimson" }}>{isZh() ? "錯誤：" : "Error:"} {error}</div>;
    if (!rows || rows.length === 0) return <div className="empty">{isZh() ? "沒有找到資料" : "No results"}</div>;

    return rows.map(r => {
      const line1 = getUmatDisplayLabel(r);
      const line2 = getUmatDisplayLabelLine2(r);
      const line3 = getUmatMetaLine(r);
      const avatar = resolveUmatAvatarSync(r.raw);

      return (
        <CardUmat
          key={r.id}
          avatar={avatar}
          line1={line1}
          line2={line2}
          line3={line3}
          onClick={() => setSelected(r)}
        />
      );
    });
  }, [rows, loading, error]);

  // detail node
  const detailNode = selected ? <DetailUmat umat={selected} onClose={() => setSelected(null)} canViewUmatAge={canViewUmatAge} /> : null;
/*   const detailNode = selected ? (() => {
    const qrData = selected.umatID || selected.id || selected.email || "";
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}`;
    const avatar = resolveUmatAvatarSync(selected.raw);
    
    return (
      <div className="detail-card">
        <div className="detail-head">
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <img src={avatar} alt={selected.fullName || selected.nickName} style={{ width: 72, height: 72, borderRadius: 8, objectFit: "cover" }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{selected.fullName || selected.nickName}</div>
              <div style={{ opacity: 0.8 }}>{selected.chineseName ? `${selected.chineseName}${' '}${capitalizePinyin(toPinyinSync(selected.chineseName))}` : ""}</div>
            </div>
          </div>
          <button className="btn-icon close-detail" onClick={() => setSelected(null)}>✕</button>
        </div>

        <div className="detail-body">
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="detail-row"><strong>Gender:</strong> {selected.gender || "-"}</div>
              <div className="detail-row" style={{ display: "none" }}>
                <strong>Usia:</strong>{" "}
                {selected.birthdate
                  ? `${Math.floor((new Date() - new Date(selected.birthdate)) / (365.25 * 24 * 60 * 60 * 1000))} tahun`
                  : "-"}
              </div>
              <div className="detail-row"><strong>Area:</strong> { (selected.area ? selected.area.name : "") || "-" }</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div className="detail-row"><strong>Mobile:</strong> {selected.mobilePhone || "-"}</div>
              <div className="detail-row"><strong>Email:</strong> {selected.email || "-"}</div>
            </div>
          </div>

          <div style={{ marginTop: 12, textAlign: "center" }}>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>QR dari Kode Umat</div>
            <img src={qrUrl} alt={`QR-${selected.umatID}`} style={{ width: 160, height: 160, display: "inline-block" }} />
            <div style={{ fontSize: 16, marginBottom: 6 }}><strong>{selected.umatID}</strong></div> 
          </div>

          <div style={{ marginTop: 12, display: "none" }}>
            <button className="btn-primary" onClick={() => alert("Edit pressed")}>Edit</button>
            <button className="btn-secondary" onClick={() => alert("Delete pressed")} style={{ marginLeft: 8 }}>Delete</button>
          </div>
        </div>
      </div>
    );
  })() : null; */

  function handleSearchChange(v) {
    setSearchValue(v);
    setPage(1); // reset page when search changes
    setSelected(null); // kosongkan detail panel saat ada perubahan search
    if (!isSearchable(v)) {
      setRows([]);
      setMeta(null);
      setError(null);
    }
  }

  function handlePrevPage() {
    if (!meta) return;
    const cur = meta.pagination?.page || page;
    if (cur > 1) setPage(cur - 1);
  }
  function handleNextPage() {
    if (!meta) return;
    const cur = meta.pagination?.page || page;
    const pageCount = meta.pagination?.pageCount || null;
    if (!pageCount || cur < pageCount) setPage(cur + 1);
  }

  // --- pagination UI (dipakai di atas & bawah) ---
  const paginationNode = (() => {
    const current = meta?.pagination?.page || page || 1;
    const pageCount = meta?.pagination?.pageCount ?? null;
    const total = meta?.pagination?.total ?? null;

    // hide pagination if we know there's only 1 page or pageCount is 1
    if (pageCount !== null && pageCount <= 1) return null;

    // if meta is null (no server paging info) and rows length <= pageSize, hide too
    if (pageCount === null && rows && rows.length <= pageSize) return null;

    const middle = //pageCount ? `Halaman ${current} dari ${pageCount}` : `Halaman ${current}${total ? ` • ${total} total` : ""}`;
      pageCount ? 
        (isZh() ? 
          `第 ${current} 頁，共 ${pageCount} 頁` : 
          `Halaman ${current} dari ${pageCount}`) : 
        "";

    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, padding: "8px 0" }}>
        <button className="btn-secondary" onClick={handlePrevPage} disabled={current <= 1}>{isZh() ? "< 上一頁" : "< Prev"}</button>
        <div style={{ padding: "4px 12px", borderRadius: 6, background: "transparent", minWidth: 180, textAlign: "center", fontWeight: 600 }}>
          {middle}
        </div>
        <button className="btn-secondary" onClick={handleNextPage} disabled={pageCount ? current >= pageCount : false}>{isZh() ? "下一頁 >" : "Next >"}</button>
      </div>
    );
  })();

  const infoNode = (
    <div className="tip-text">
      {meta ? 
        (isZh() ? 
          `顯示第 ${meta.pagination.page} / ${meta.pagination.pageCount} 頁 — 共 ${meta.pagination.total} 筆` : 
          `Menampilkan halaman ${meta.pagination.page} / ${meta.pagination.pageCount} — ${meta.pagination.total} total`) : 
        (isZh() ? 
          `找到 ${rows.length} 筆資料` :
          `${rows.length} data ditemukan`)
      }
    </div>
  );

  const filterNode = (
    <div>
      <div className="filter-head"><strong>{ isZh() ? "篩選條件" : "Filters"}</strong></div>
      <div className="filter-controls">
        <label>
          { isZh() ? "搜尋：" : "Cari:"}
          <input value={searchValue} onChange={(e) => handleSearchChange(e.target.value)} placeholder={ isZh() ? "編號 / 拼音 / 姓名 / 暱稱 / 電子郵件" : "kode / pinyin / nama / nickname / email"} />
        </label>
        <div style={{ marginTop: 12 }}>
          <button className="btn-secondary" onClick={() => handleSearchChange("")}>{ isZh() ? "重設" : "Reset"}</button>
        </div>
      </div>
    </div>
  );

  const headerActions = (
    <>
      { hasUmatPermission(getUser()?.email, PERMISSIONS.UMATDATA_EDIT) && (
        <button className="btn-icon" onClick={() => history.push("/DaftarUmat")} title={ isZh() ? "新增" : "Add"}>＋</button>
      )}
    </>
  );

  const mainWithPaging = (
    <>
      {paginationNode}
      <div>
        {listNode}
      </div>
      {paginationNode}
    </>
  );

  return (
    <ContentLayout
      title={ isZh() ? "道親資料" : "DATA UMAT"}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      filterNode={filterNode}
      infoNode={infoNode}
      mainNode={mainWithPaging}
      detailNode={detailNode}
      headerActions={headerActions}
      onCloseDetail={() => setSelected(null)}
    />
  );
}
